import React, { useState, useMemo, useEffect, useCallback } from "react";
import { format } from "date-fns";
import axios from "axios";
import {
  LockKeyhole, CalendarDays, Users, XCircle, Search, LogOut, Loader2, Mail,
  Printer, ClipboardList, ListChecks, Download, Pencil, Upload, FileCheck2, CheckCircle2, KeyRound,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { TESTS, COUNTRIES } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("mbs_admin_token") || "");
  const [passcode, setPasscode] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTest, setFilterTest] = useState("all");
  const [search, setSearch] = useState("");
  const [rangeFrom, setRangeFrom] = useState(null);
  const [rangeTo, setRangeTo] = useState(null);
  // Roster state
  const [rosterDate, setRosterDate] = useState(new Date());
  const [roster, setRoster] = useState(null);
  const [loadingRoster, setLoadingRoster] = useState(false);
  // Head shot photos (PUBLIC test testees)
  const [photoMap, setPhotoMap] = useState({});
  const [photoView, setPhotoView] = useState(null); // {data, name}
  // Booking edit dialog
  const [editBooking, setEditBooking] = useState(null); // deep working copy
  const [savingEdit, setSavingEdit] = useState(false);
  // CSV import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [importTest, setImportTest] = useState("bridge-lr");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Certified copy requests
  const [copyRequests, setCopyRequests] = useState([]);
  const [loadingCopies, setLoadingCopies] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  // Blackout dates
  const [blackoutList, setBlackoutList] = useState([]);
  const [blackoutDate, setBlackoutDate] = useState(null);
  const [blackoutReason, setBlackoutReason] = useState("");
  const [addingBlackout, setAddingBlackout] = useState(false);
  // Change passcode dialog
  const [pwdOpen, setPwdOpen] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const url = err.config?.url || "";
        if (err.response?.status === 401 && url.includes("/admin/") && !url.includes("/admin/login")) {
          sessionStorage.removeItem("mbs_admin_token");
          setToken("");
          toast.error("Session expired. Please sign in again.");
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const changePasscode = async () => {
    if (changingPwd) return;
    if (!curPwd) { toast.error("Enter your current passcode."); return; }
    if (newPwd.trim().length < 6) { toast.error("New passcode must be at least 6 characters."); return; }
    if (newPwd !== confirmPwd) { toast.error("New passcodes do not match."); return; }
    setChangingPwd(true);
    try {
      await axios.post(
        `${API}/admin/change-passcode`,
        { current_passcode: curPwd, new_passcode: newPwd },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Passcode updated. Other signed-in devices were logged out.");
      setPwdOpen(false);
      setCurPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update passcode.");
    } finally {
      setChangingPwd(false);
    }
  };

  const fetchBlackouts = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/blackouts`, { headers: { "X-Admin-Token": tok } });
      setBlackoutList(res.data);
    } catch { /* ignore */ }
  }, []);

  const addBlackout = async () => {
    if (!blackoutDate) { toast.error("Please pick a date."); return; }
    setAddingBlackout(true);
    try {
      const res = await axios.post(
        `${API}/admin/blackouts`,
        { date: format(blackoutDate, "yyyy-MM-dd"), reason: blackoutReason.trim() || null },
        { headers: { "X-Admin-Token": token } }
      );
      setBlackoutList((prev) => [...prev, res.data].sort((a, b) => a.date.localeCompare(b.date)));
      setBlackoutDate(null);
      setBlackoutReason("");
      toast.success("Blackout date added — online booking is now blocked for that date.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add the blackout date.");
    } finally {
      setAddingBlackout(false);
    }
  };

  const removeBlackout = async (id) => {
    try {
      await axios.delete(`${API}/admin/blackouts/${id}`, { headers: { "X-Admin-Token": token } });
      setBlackoutList((prev) => prev.filter((b) => b.id !== id));
      toast.success("Blackout date removed — bookings are open again for that date.");
    } catch {
      toast.error("Could not remove the blackout date.");
    }
  };

  const fetchCopyRequests = useCallback(async (tok) => {
    setLoadingCopies(true);
    try {
      const res = await axios.get(`${API}/admin/copy-requests`, { headers: { "X-Admin-Token": tok } });
      setCopyRequests(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("mbs_admin_token");
        setToken("");
      } else {
        toast.error("Could not load copy requests.");
      }
    } finally {
      setLoadingCopies(false);
    }
  }, []);

  const completeCopyRequest = async (id) => {
    setCompletingId(id);
    try {
      const res = await axios.put(`${API}/admin/copy-requests/${id}/complete`, {}, { headers: { "X-Admin-Token": token } });
      setCopyRequests((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      toast.success("Request marked as completed.");
    } catch {
      toast.error("Could not update the request.");
    } finally {
      setCompletingId(null);
    }
  };

  const markCopyPaid = async (id) => {
    setCompletingId(id);
    try {
      const res = await axios.put(`${API}/admin/copy-requests/${id}/mark-paid`, {}, { headers: { "X-Admin-Token": token } });
      setCopyRequests((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      toast.success("Request marked as paid.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark the request as paid.");
    } finally {
      setCompletingId(null);
    }
  };

  const [checkingOrder, setCheckingOrder] = useState(null);
  const checkGateway = async (orderId) => {
    setCheckingOrder(orderId);
    try {
      const res = await axios.get(`${API}/admin/payments/${orderId}/check`, { headers: { "X-Admin-Token": token } });
      const labels = {
        captured: "Captured — payment confirmed",
        captured_mismatch: "Captured but amount/currency mismatch — flagged Needs Review",
        declined: "Declined by the bank",
        voided: "Voided",
        returned: "Refunded",
        pending: "Still pending at the gateway",
        not_found: "No transaction found at the gateway",
      };
      toast.success(`Gateway: ${labels[res.data.outcome] || res.data.outcome}`);
      fetchBookings(token);
      fetchCopyRequests(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gateway check failed.");
    } finally {
      setCheckingOrder(null);
    }
  };

  const markBookingPaid = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/bookings/${id}/mark-paid`, {}, { headers: { "X-Admin-Token": token } });
      setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      toast.success("Booking marked as paid.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark the booking as paid.");
    }
  };

  const fetchBookings = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: { "X-Admin-Token": tok } });
      setBookings(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("mbs_admin_token");
        setToken("");
      } else {
        toast.error("Could not load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoster = useCallback(async (tok, d) => {
    if (!d) return;
    setLoadingRoster(true);
    try {
      const res = await axios.get(`${API}/admin/roster`, {
        params: { date: format(d, "yyyy-MM-dd") },
        headers: { "X-Admin-Token": tok },
      });
      setRoster(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("mbs_admin_token");
        setToken("");
      } else {
        toast.error("Could not load the roster.");
      }
    } finally {
      setLoadingRoster(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchBookings(token);
      fetchRoster(token, rosterDate);
      fetchCopyRequests(token);
      fetchBlackouts(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && rosterDate) fetchRoster(token, rosterDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterDate]);

  // Fetch head shot photos for PUBLIC testees in the loaded roster
  useEffect(() => {
    if (!roster || !token) return;
    const ids = [];
    roster.slots.forEach((s) =>
      s.testees.forEach((t) => {
        if (t.photo_id && photoMap[t.photo_id] === undefined && !ids.includes(t.photo_id)) ids.push(t.photo_id);
      })
    );
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await axios.get(`${API}/admin/photos/${id}`, { headers: { "X-Admin-Token": token } });
            return [id, res.data.data];
          } catch {
            return [id, null];
          }
        })
      );
      if (!cancelled) setPhotoMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, token]);

  const login = async () => {
    if (!passcode.trim() || loggingIn) return;
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem("mbs_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back!");
    } catch {
      toast.error("Incorrect passcode.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/cancel`, {}, { headers: { "X-Admin-Token": token } });
      toast.success("Booking cancelled.");
      fetchBookings(token);
      fetchRoster(token, rosterDate);
    } catch {
      toast.error("Could not cancel booking.");
    }
  };

  // ---- Staff booking edit ----
  const openEdit = (b) => setEditBooking(JSON.parse(JSON.stringify(b)));

  const updateEditEmployer = (field, value) =>
    setEditBooking((prev) => ({ ...prev, employer: { ...prev.employer, [field]: value } }));

  const updateEditTestee = (i, field, value) =>
    setEditBooking((prev) => ({
      ...prev,
      testees: prev.testees.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)),
    }));

  const saveEdit = async () => {
    if (savingEdit || !editBooking) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editBooking.employer?.email || "")) {
      toast.error("Employer/Company Email must be a valid email address.");
      return;
    }
    const dobRe = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
    for (let i = 0; i < editBooking.testees.length; i++) {
      const t = editBooking.testees[i];
      if (!t.full_name?.trim()) { toast.error(`Testee ${i + 1}: Full Name is required.`); return; }
      if (!dobRe.test((t.dob || "").trim())) { toast.error(`Testee ${i + 1}: Date of Birth must be mm/dd/yyyy.`); return; }
      if (!t.native_country?.trim()) { toast.error(`Testee ${i + 1}: Native Country is required.`); return; }
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/admin/bookings/${editBooking.id}`,
        {
          employer: {
            name: editBooking.employer?.name || "",
            contact: editBooking.employer?.contact || "",
            email: editBooking.employer?.email || "",
          },
          testees: editBooking.testees.map((t) => ({
            full_name: t.full_name,
            dob: (t.dob || "").trim(),
            native_country: t.native_country,
            worker_ref: t.worker_ref || "",
            taken_before: t.taken_before || "No",
            work_permit: t.work_permit || "Yes",
            email: t.email || "",
            photo_filename: t.photo_filename || "",
          })),
        },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Booking updated.");
      setEditBooking(null);
      fetchBookings(token);
      fetchRoster(token, rosterDate);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update the booking.");
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = useMemo(() => {
    return bookings
      .filter((b) => (filterTest === "all" ? true : b.test_id === filterTest))
      .filter((b) => {
        if (rangeFrom && b.date < format(rangeFrom, "yyyy-MM-dd")) return false;
        if (rangeTo && b.date > format(rangeTo, "yyyy-MM-dd")) return false;
        return true;
      })
      .filter((b) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
          (b.confirmation_number || "").toLowerCase().includes(q) ||
          (b.employer?.name || "").toLowerCase().includes(q) ||
          (b.testees || []).some((t) => (t.full_name || "").toLowerCase().includes(q))
        );
      });
  }, [bookings, filterTest, search, rangeFrom, rangeTo]);

  const stats = useMemo(() => {
    const active = bookings.filter((b) => b.status !== "cancelled");
    const seats = active.reduce((s, b) => s + Number(b.quantity || 1), 0);
    return { total: active.length, seats, cancelled: bookings.length - active.length };
  }, [bookings]);

  // ---- Roster CSV export (matches the MBS roster spreadsheet format) ----
  const csvField = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const stripLeadingZeros = (dob) => {
    // stored as MM/dd/yyyy -> export as M/D/yyyy like the sample file
    const parts = String(dob || "").split("/");
    if (parts.length !== 3) return dob || "";
    return `${parseInt(parts[0], 10)}/${parseInt(parts[1], 10)}/${parts[2]}`;
  };

  const exportMbsList = async () => {
    if (!rosterDate) return;
    const dateStr = format(rosterDate, "yyyy-MM-dd");
    try {
      const res = await axios.get(`${API}/admin/roster/export-mbs-list`, {
        params: { date: dateStr },
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MBS List - ${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("MBS List exported.");
    } catch {
      toast.error("Could not export the MBS list.");
    }
  };

  const exportMbsCodes = async () => {
    if (!rosterDate) return;
    const dateStr = format(rosterDate, "yyyy-MM-dd");
    try {
      const res = await axios.get(`${API}/admin/roster/export-mbs-codes`, {
        params: { date: dateStr },
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MBS Codes - ${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("MBS Codes exported.");
    } catch {
      toast.error("Could not export the MBS codes list.");
    }
  };


  const exportRosterCsv = () => {
    if (!roster) return;
    const header = [
      "Employer/Company Name",
      "Employer Contact Number",
      "Email",
      "Full Name",
      "Date of Birth",
      "Native Country",
      "WORKER REFERENCE # (LOCATED ON WORK PERMIT LICENSE)",
      "HAVE YOU TAKEN THIS TEST BEFORE?",
      "This test is being taken for a work permit application. ",
      "DATE",
      "TIME",
    ];
    const dateDMY = format(rosterDate, "dd/MM/yyyy");
    const rows = [];
    roster.slots.forEach((slot) => {
      slot.testees.forEach((t) => {
        rows.push([
          t.employer_name || "-",
          t.employer_contact || "-",
          t.employer_email || "",
          t.full_name || "",
          stripLeadingZeros(t.dob),
          t.native_country || "",
          t.worker_ref || "",
          t.taken_before || "",
          t.work_permit || "",
          dateDMY,
          slot.time,
        ]);
      });
    });
    if (rows.length === 0) {
      toast.error("No testees booked on this date to export.");
      return;
    }
    const csv = [header, ...rows].map((r) => r.map(csvField).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MBS-Roster-${format(rosterDate, "yyyy-MM-dd")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Roster exported to CSV.");
  };

  const exportBookingsCsv = () => {
    const header = [
      "Employer/Company Name",
      "Employer Contact Number",
      "Email",
      "Full Name",
      "Date of Birth",
      "Native Country",
      "WORKER REFERENCE # (LOCATED ON WORK PERMIT LICENSE)",
      "HAVE YOU TAKEN THIS TEST BEFORE?",
      "This test is being taken for a work permit application. ",
      "DATE",
      "TIME",
    ];
    const rows = [];
    const sorted = [...filtered].sort((a, b) =>
      a.date === b.date ? (a.time_slot > b.time_slot ? 1 : -1) : a.date > b.date ? 1 : -1
    );
    sorted.forEach((b) => {
      if (b.status === "cancelled") return;
      const dateDMY = format(new Date(b.date + "T12:00:00"), "dd/MM/yyyy");
      (b.testees || []).forEach((t) => {
        rows.push([
          b.employer?.name || "-",
          b.employer?.contact || "-",
          b.employer?.email || "",
          t.full_name || "",
          stripLeadingZeros(t.dob),
          t.native_country || "",
          t.worker_ref || "",
          t.taken_before || "",
          t.work_permit || "",
          dateDMY,
          b.time_slot,
        ]);
      });
    });
    if (rows.length === 0) {
      toast.error("No active bookings match the current filters to export.");
      return;
    }
    const csv = [header, ...rows].map((r) => r.map(csvField).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const suffix =
      rangeFrom || rangeTo
        ? `-${rangeFrom ? format(rangeFrom, "yyyy-MM-dd") : "start"}-to-${rangeTo ? format(rangeTo, "yyyy-MM-dd") : "end"}`
        : "-all";
    a.download = `MBS-Bookings${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Bookings exported to CSV.");
  };

  // ---- CSV import (bulk-load existing bookings) ----
  const parseCsv = (text) => {
    const rows = [];
    let cur = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') inQuotes = true;
      else if (c === ",") { cur.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        cur.push(field); field = "";
        if (cur.some((f) => f.trim() !== "")) rows.push(cur);
        cur = [];
      } else field += c;
    }
    if (field !== "" || cur.length) { cur.push(field); if (cur.some((f) => f.trim() !== "")) rows.push(cur); }
    return rows;
  };

  const mapHeader = (h) => {
    const l = h.trim().toLowerCase();
    if (l.includes("employer/company name")) return "employer_name";
    if (l.includes("employer contact")) return "employer_contact";
    if (l === "email") return "employer_email";
    if (l.includes("full name")) return "full_name";
    if (l.includes("date of birth")) return "dob";
    if (l.includes("native country")) return "native_country";
    if (l.includes("worker reference")) return "worker_ref";
    if (l.includes("taken this test before")) return "taken_before";
    if (l.includes("work permit application")) return "work_permit";
    if (l === "date") return "date";
    if (l === "time") return "time_slot";
    return null;
  };

  const handleImportFile = (file) => {
    if (!file) return;
    setImportResult(null);
    const process = (text) => {
      const grid = parseCsv(text);
      if (grid.length < 2) { toast.error("The CSV file appears to be empty."); return; }
      const keys = grid[0].map(mapHeader);
      const rows = grid.slice(1).map((cells) => {
        const obj = {};
        keys.forEach((k, i) => { if (k) obj[k] = (cells[i] || "").trim(); });
        return obj;
      }).filter((r) => r.full_name && r.date && r.time_slot);
      if (!rows.length) { toast.error("No valid booking rows found. Please check the CSV columns."); return; }
      setImportRows(rows);
      setImportFileName(file.name);
      toast.success(`${rows.length} booking row(s) ready to import.`);
    };
    const utf8Reader = new FileReader();
    utf8Reader.onload = () => {
      const text = utf8Reader.result || "";
      if (text.includes("\uFFFD")) {
        const latinReader = new FileReader();
        latinReader.onload = () => process(latinReader.result || "");
        latinReader.readAsText(file, "ISO-8859-1");
      } else process(text);
    };
    utf8Reader.readAsText(file, "UTF-8");
  };

  const runImport = async () => {
    if (!importRows.length || importing) return;
    setImporting(true);
    try {
      const res = await axios.post(
        `${API}/admin/import-bookings`,
        { test_id: importTest, rows: importRows },
        { headers: { "X-Admin-Token": token } }
      );
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} of ${res.data.total} bookings.`);
      fetchBookings(token);
      fetchRoster(token, rosterDate);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed. Please check the file and try again.");
    } finally {
      setImporting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] bg-[#F6FAFB] flex items-center justify-center px-4" data-testid="admin-login">
        <Card className="rounded-2xl border-slate-100 shadow-lg w-full max-w-md">
          <CardContent className="p-9">
            <div className="w-14 h-14 rounded-2xl bg-[#0A2540] flex items-center justify-center mb-6 mx-auto">
              <LockKeyhole className="text-[#39C1D0]" size={26} />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0A2540] text-center">Staff Admin Access</h1>
            <p className="text-sm text-slate-500 text-center mt-2">Enter the staff passcode to manage bookings.</p>
            <div className="mt-7">
              <Label className="font-bold text-[#0A2540]">Passcode</Label>
              <Input
                data-testid="admin-passcode-input"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                className="mt-2 rounded-lg"
                placeholder="Enter passcode"
              />
              <Button
                data-testid="admin-login-btn"
                onClick={login}
                disabled={loggingIn}
                className="w-full mt-5 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg h-11"
              >
                {loggingIn ? <Loader2 size={17} className="animate-spin" /> : "Sign In"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-[#F6FAFB] min-h-screen py-10" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 print:hidden">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">Booking Dashboard</h1>
            <p className="text-slate-500 text-sm mt-1">Manage all test registrations.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              data-testid="admin-change-passcode-btn"
              onClick={() => setPwdOpen(true)}
              className="border-slate-300 text-slate-600 font-bold rounded-lg"
            >
              <KeyRound size={15} className="mr-2" /> Change Passcode
            </Button>
            <Button
              variant="outline"
              data-testid="admin-logout-btn"
              onClick={() => { sessionStorage.removeItem("mbs_admin_token"); setToken(""); }}
              className="border-slate-300 text-slate-600 font-bold rounded-lg"
            >
              <LogOut size={15} className="mr-2" /> Sign Out
            </Button>
          </div>
        </div>

        <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) { setCurPwd(""); setNewPwd(""); setConfirmPwd(""); } }}>
          <DialogContent className="max-w-sm rounded-2xl" data-testid="change-passcode-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0A2540] font-extrabold">Change Admin Passcode</DialogTitle>
              <DialogDescription className="text-slate-500 text-sm">
                All other signed-in devices will be logged out after the change.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label className="font-bold text-[#0A2540]">Current Passcode</Label>
                <Input
                  data-testid="current-passcode-input"
                  type="password"
                  value={curPwd}
                  onChange={(e) => setCurPwd(e.target.value)}
                  className="rounded-lg"
                  placeholder="Enter current passcode"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold text-[#0A2540]">New Passcode</Label>
                <Input
                  data-testid="new-passcode-input"
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  className="rounded-lg"
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold text-[#0A2540]">Confirm New Passcode</Label>
                <Input
                  data-testid="confirm-passcode-input"
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && changePasscode()}
                  className="rounded-lg"
                  placeholder="Re-enter new passcode"
                />
              </div>
              <Button
                data-testid="save-passcode-btn"
                onClick={changePasscode}
                disabled={changingPwd}
                className="w-full bg-[#0E9AAA] hover:bg-[#0C8694] text-white font-bold rounded-lg"
              >
                {changingPwd ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={15} className="mr-2" />}
                Update Passcode
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 print:hidden">
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center"><CalendarDays className="text-[#0E9AAA]" size={22} /></div>
              <div><div className="text-2xl font-extrabold text-[#0A2540]" data-testid="stat-active">{stats.total}</div><div className="text-xs text-slate-500 font-semibold">Active Bookings</div></div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center"><Users className="text-[#0E9AAA]" size={22} /></div>
              <div><div className="text-2xl font-extrabold text-[#0A2540]">{stats.seats}</div><div className="text-xs text-slate-500 font-semibold">Total Testees Booked</div></div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center"><XCircle className="text-red-400" size={22} /></div>
              <div><div className="text-2xl font-extrabold text-[#0A2540]">{stats.cancelled}</div><div className="text-xs text-slate-500 font-semibold">Cancelled</div></div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="mb-6 bg-white border border-slate-100 rounded-xl p-1 h-auto print:hidden">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-lg font-bold px-6 py-2.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              <ListChecks size={16} className="mr-2" /> All Bookings
            </TabsTrigger>
            <TabsTrigger value="roster" data-testid="tab-roster" className="rounded-lg font-bold px-6 py-2.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              <ClipboardList size={16} className="mr-2" /> Daily Roster
            </TabsTrigger>
            <TabsTrigger value="copy-requests" data-testid="tab-copy-requests" className="rounded-lg font-bold px-6 py-2.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              <FileCheck2 size={16} className="mr-2" /> Copy Requests
              {copyRequests.filter((r) => r.status === "paid").length > 0 && (
                <span className="ml-2 bg-[#0E9AAA] text-white text-[11px] font-extrabold rounded-full px-2 py-0.5">
                  {copyRequests.filter((r) => r.status === "paid").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="blackouts" data-testid="tab-blackouts" className="rounded-lg font-bold px-6 py-2.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">
              <CalendarDays size={16} className="mr-2" /> Blackout Dates
            </TabsTrigger>
          </TabsList>

          {/* ------------------- BOOKINGS TAB ------------------- */}
          <TabsContent value="bookings">
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="admin-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, employer or confirmation #"
                  className="pl-10 rounded-lg bg-white"
                />
              </div>
              <Select value={filterTest} onValueChange={setFilterTest}>
                <SelectTrigger className="w-64 rounded-lg bg-white" data-testid="admin-test-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tests</SelectItem>
                  {TESTS.map((t) => <SelectItem key={t.id} value={t.id}>{t.shortName}</SelectItem>)}
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="range-from-btn" className="rounded-lg bg-white font-bold text-[#0A2540] border-slate-200">
                    <CalendarDays size={15} className="mr-2 text-[#0E9AAA]" />
                    {rangeFrom ? `From: ${format(rangeFrom, "MMM d, yyyy")}` : "From date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={rangeFrom} onSelect={setRangeFrom} />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="range-to-btn" className="rounded-lg bg-white font-bold text-[#0A2540] border-slate-200">
                    <CalendarDays size={15} className="mr-2 text-[#0E9AAA]" />
                    {rangeTo ? `To: ${format(rangeTo, "MMM d, yyyy")}` : "To date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={rangeTo} onSelect={setRangeTo} />
                </PopoverContent>
              </Popover>
              {(rangeFrom || rangeTo) && (
                <Button
                  variant="ghost"
                  onClick={() => { setRangeFrom(null); setRangeTo(null); }}
                  className="text-slate-500 font-bold"
                >
                  Clear Dates
                </Button>
              )}
              <Button
                data-testid="bookings-export-btn"
                onClick={exportBookingsCsv}
                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg"
              >
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
              <Button
                data-testid="bookings-import-btn"
                onClick={() => { setImportOpen(true); setImportResult(null); setImportRows([]); setImportFileName(""); }}
                variant="outline"
                className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white font-bold rounded-lg transition-colors"
              >
                <Upload size={16} className="mr-2" /> Import CSV
              </Button>
            </div>

            <Card className="rounded-2xl border-slate-100 shadow-sm overflow-hidden">
              {loading ? (
                <CardContent className="p-14 text-center text-slate-400 font-semibold flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" /> Loading bookings...
                </CardContent>
              ) : filtered.length === 0 ? (
                <CardContent className="p-14 text-center text-slate-400 font-semibold">
                  No bookings found. New registrations will appear here.
                </CardContent>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F6FAFB]">
                        <TableHead className="font-extrabold text-[#0A2540]">Confirmation #</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Test</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Date & Time</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Testee(s)</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Employer</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Qty</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Payment</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Email</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Status</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540] text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((b) => (
                        <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                          <TableCell className="font-bold text-[#0E9AAA] whitespace-nowrap">{b.confirmation_number}</TableCell>
                          <TableCell className="text-slate-600 max-w-[180px]">{TESTS.find((t) => t.id === b.test_id)?.shortName || b.test_name}</TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">
                            {format(new Date(b.date + "T12:00:00"), "MMM d, yyyy")}<br />
                            <span className="text-xs text-slate-400">{b.time_slot}</span>
                          </TableCell>
                          <TableCell className="text-slate-600 max-w-[200px]">{(b.testees || []).map((t) => t.full_name).join(", ")}</TableCell>
                          <TableCell className="text-slate-600">{b.employer?.name || "—"}</TableCell>
                          <TableCell className="text-slate-600">{b.quantity}</TableCell>
                          <TableCell>
                            {b.payment_status === "paid_online" ? (
                              <Badge className="bg-emerald-50 text-emerald-600 hover:bg-emerald-50 border border-emerald-200 whitespace-nowrap" data-testid={`payment-badge-${b.id}`}
                                title={b.payment_verification === "success_return" ? "Auto-confirmed from the bank's success redirect — cross-check in the Plug'n Pay portal if in doubt" : b.payment_verification === "admin_manual" ? "Manually marked paid by staff" : "Verified from gateway response data"}>
                                Paid Online
                              </Badge>
                            ) : b.payment_status === "processing" ? (
                              <Badge className="bg-amber-50 text-amber-600 hover:bg-amber-50 border border-amber-200 whitespace-nowrap">Processing</Badge>
                            ) : b.payment_status === "needs_review" ? (
                              <div className="flex flex-col items-start gap-1">
                                <Badge className="bg-red-50 text-red-600 hover:bg-red-50 border border-red-200 whitespace-nowrap" title="Payment reported but amount/currency could not be verified — check the Plug'n Pay portal, then use Mark Paid">Needs Review</Badge>
                                <button onClick={() => markBookingPaid(b.id)} className="text-[11px] font-bold text-[#0E9AAA] hover:text-[#0C8695] hover:underline whitespace-nowrap">Mark Paid</button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-start gap-1">
                                <Badge variant="outline" className="text-slate-600 border-slate-300 whitespace-nowrap font-semibold">
                                  At Check-in
                                </Badge>
                                {b.status !== "cancelled" && (
                                  <button
                                    data-testid={`mark-paid-btn-${b.id}`}
                                    onClick={() => markBookingPaid(b.id)}
                                    className="text-[11px] font-bold text-[#0E9AAA] hover:text-[#0C8695] hover:underline whitespace-nowrap"
                                    title="Use after verifying the transaction in the Plug'n Pay portal"
                                  >
                                    Mark Paid
                                  </button>
                                )}
                              </div>
                            )}
                            {b.pnp_order_id && (
                              <button
                                data-testid={`gateway-check-btn-${b.id}`}
                                onClick={() => checkGateway(b.pnp_order_id)}
                                disabled={checkingOrder === b.pnp_order_id}
                                className="mt-1 block text-[11px] font-semibold text-slate-400 hover:text-[#0C8695] hover:underline whitespace-nowrap"
                                title="Verify this payment against Plug'n Pay (query_trans)"
                              >
                                {checkingOrder === b.pnp_order_id ? "Checking…" : "Check with gateway"}
                              </button>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${b.email_status === "sent" ? "text-emerald-600" : "text-slate-400"}`}>
                              <Mail size={12} /> {b.email_status === "sent" ? "Sent" : b.email_status === "failed" ? "Failed" : "Not sent"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={b.status === "cancelled" ? "bg-red-50 text-red-500 hover:bg-red-50 border border-red-100" : "bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC]"}>
                              {b.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {b.status !== "cancelled" && (
                              <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEdit(b)}
                                className="border-slate-200 text-[#0A2540] hover:bg-slate-50 font-bold rounded-lg"
                                data-testid={`edit-btn-${b.id}`}
                              >
                                <Pencil size={13} className="mr-1.5" /> Edit
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 font-bold rounded-lg" data-testid={`cancel-btn-${b.id}`}>
                                    Cancel
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-2xl">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will cancel booking {b.confirmation_number} and free up {b.quantity} seat(s) for {b.time_slot} on {b.date_display || b.date}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-lg font-bold">Keep Booking</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleCancel(b.id)} className="bg-red-500 hover:bg-red-600 rounded-lg font-bold">
                                      Yes, Cancel It
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ------------------- DAILY ROSTER TAB ------------------- */}
          <TabsContent value="roster">
            <div className="flex flex-wrap items-center gap-4 mb-6 print:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="roster-date-btn"
                    className="rounded-lg bg-white font-bold text-[#0A2540] border-slate-200"
                  >
                    <CalendarDays size={16} className="mr-2 text-[#0E9AAA]" />
                    {rosterDate ? format(rosterDate, "EEEE, MMMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={rosterDate}
                    onSelect={(d) => d && setRosterDate(d)}
                    disabled={(d) => d.getDay() === 0 || d.getDay() === 6}
                  />
                </PopoverContent>
              </Popover>
              <Button
                data-testid="roster-print-btn"
                onClick={() => window.print()}
                className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg"
              >
                <Printer size={16} className="mr-2" /> Print Roster
              </Button>
              <Button
                data-testid="roster-export-btn"
                onClick={exportRosterCsv}
                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg"
              >
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
              <Button
                data-testid="roster-export-mbs-btn"
                onClick={exportMbsList}
                variant="outline"
                className="border-[#0E9AAA] text-[#0E9AAA] hover:bg-[#E8F7F9] hover:text-[#0C8695] font-bold rounded-lg"
              >
                <Download size={16} className="mr-2" /> Export MBS List
              </Button>
              <Button
                data-testid="roster-export-codes-btn"
                onClick={exportMbsCodes}
                variant="outline"
                className="border-[#0E9AAA] text-[#0E9AAA] hover:bg-[#E8F7F9] hover:text-[#0C8695] font-bold rounded-lg"
              >
                <Download size={16} className="mr-2" /> Export MBS Codes
              </Button>
            </div>

            {loadingRoster ? (
              <Card className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-14 text-center text-slate-400 font-semibold flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" /> Loading roster...
                </CardContent>
              </Card>
            ) : roster ? (
              <div className="print-area" data-testid="roster-content">
                {/* Print-only header */}
                <div className="hidden print:block mb-4">
                  <h1 className="text-2xl font-extrabold text-black">Modern Business Solutions — Daily Test Roster</h1>
                  <p className="text-sm text-black mt-1">{roster.date_display} • 93 Hospital Rd, SIGMA Building, 1st Flr, George Town • 1-345-924-6887</p>
                </div>
                <div className="space-y-8">
                  {roster.slots.map((slot) => (
                    <Card key={slot.time} className="rounded-2xl border-slate-100 shadow-sm overflow-hidden print:shadow-none print:border-black/20 print:rounded-none">
                      <div className="bg-[#0A2540] px-6 py-4 flex items-center justify-between print:bg-white print:border-b print:border-black/30">
                        <div className="font-extrabold text-white text-lg print:text-black">{slot.time} Session</div>
                        <div className="text-sm font-bold text-[#39C1D0] print:text-black">
                          {slot.seats_filled} / {slot.capacity} seats filled
                        </div>
                      </div>
                      {slot.testees.length === 0 ? (
                        <CardContent className="p-8 text-center text-slate-400 font-semibold">
                          No testees booked for this session.
                        </CardContent>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-[#F6FAFB] print:bg-white">
                              <TableHead className="font-extrabold text-[#0A2540] w-10">#</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540] w-16">Photo</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Testee Name</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Test</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Native Country</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Worker Ref #</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Employer</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Confirmation #</TableHead>
                              <TableHead className="font-extrabold text-[#0A2540]">Checked In</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {slot.testees.map((t, i) => (
                              <TableRow key={`${t.confirmation_number}-${i}`}>
                                <TableCell className="text-slate-400 font-semibold">{i + 1}</TableCell>
                                <TableCell>
                                  {t.photo_id ? (
                                    photoMap[t.photo_id] ? (
                                      <button
                                        data-testid={`roster-photo-${t.photo_id}`}
                                        onClick={() => setPhotoView({ data: photoMap[t.photo_id], name: t.full_name })}
                                        className="block rounded-md overflow-hidden border border-slate-200 hover:ring-2 hover:ring-[#0E9AAA] transition-shadow"
                                        title="Click to enlarge"
                                      >
                                        <img
                                          src={photoMap[t.photo_id]}
                                          alt={`Head shot of ${t.full_name}`}
                                          className="h-12 w-10 object-cover"
                                        />
                                      </button>
                                    ) : (
                                      <Loader2 size={16} className="animate-spin text-slate-300" />
                                    )
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="font-bold text-[#0A2540]">{t.full_name}</TableCell>
                                <TableCell className="text-slate-600">{t.test_name}</TableCell>
                                <TableCell className="text-slate-600">{t.native_country}</TableCell>
                                <TableCell className="text-slate-600">{t.worker_ref}</TableCell>
                                <TableCell className="text-slate-600">{t.employer_name || "—"}</TableCell>
                                <TableCell className="text-[#0E9AAA] font-bold">{t.confirmation_number}</TableCell>
                                <TableCell><span className="inline-block w-5 h-5 border-2 border-slate-300 rounded print:border-black" /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            ) : null}
          </TabsContent>

          {/* ------------------- COPY REQUESTS TAB ------------------- */}
          <TabsContent value="copy-requests">
            {loadingCopies ? (
              <Card className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-14 text-center text-slate-400 font-semibold flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" /> Loading copy requests...
                </CardContent>
              </Card>
            ) : copyRequests.length === 0 ? (
              <Card className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-14 text-center text-slate-400 font-semibold" data-testid="copy-requests-empty">
                  No certified copy requests yet.
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-2xl border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <Table data-testid="copy-requests-table">
                    <TableHeader>
                      <TableRow className="bg-[#F6FAFB]">
                        <TableHead className="font-extrabold text-[#0A2540]">Request #</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Requested</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Testee Name</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">DOB</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Contact</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Copies</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Payment</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Status</TableHead>
                        <TableHead className="font-extrabold text-[#0A2540]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {copyRequests.map((r) => (
                        <TableRow key={r.id} data-testid={`copy-request-row-${r.id}`}>
                          <TableCell className="font-extrabold text-[#0A2540] whitespace-nowrap">{r.request_number}</TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">
                            {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy") : "—"}
                          </TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">{r.full_name}</TableCell>
                          <TableCell className="text-slate-600 whitespace-nowrap">{r.dob}</TableCell>
                          <TableCell className="text-slate-600">
                            <div className="text-xs">{r.email}</div>
                            <div className="text-xs text-slate-500">{r.phone}</div>
                          </TableCell>
                          <TableCell className="text-slate-600">{r.quantity}</TableCell>
                          <TableCell>
                            {r.payment_status === "paid_online" ? (
                              <Badge className="bg-emerald-50 text-emerald-600 hover:bg-emerald-50 border border-emerald-200 whitespace-nowrap">
                                Paid CI ${Number(r.amount_paid || r.online_total).toFixed(2)}
                              </Badge>
                            ) : r.payment_status === "processing" ? (
                              <Badge className="bg-amber-50 text-amber-600 hover:bg-amber-50 border border-amber-200 whitespace-nowrap">Processing</Badge>
                            ) : r.payment_status === "needs_review" ? (
                              <div className="flex flex-col items-start gap-1">
                                <Badge className="bg-red-50 text-red-600 hover:bg-red-50 border border-red-200 whitespace-nowrap" title="Payment reported but could not be verified — check the Plug'n Pay portal, then use Mark Paid">Needs Review</Badge>
                                <button onClick={() => markCopyPaid(r.id)} className="text-[11px] font-bold text-[#0E9AAA] hover:text-[#0C8695] hover:underline whitespace-nowrap">Mark Paid</button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-start gap-1">
                                <Badge variant="outline" className="text-slate-500 border-slate-300 whitespace-nowrap font-semibold">
                                  Awaiting Payment
                                </Badge>
                                <button
                                  data-testid={`mark-copy-paid-btn-${r.id}`}
                                  onClick={() => markCopyPaid(r.id)}
                                  className="text-[11px] font-bold text-[#0E9AAA] hover:text-[#0C8695] hover:underline whitespace-nowrap"
                                  title="Use after verifying the transaction in the Plug'n Pay portal"
                                >
                                  Mark Paid
                                </button>
                              </div>
                            )}
                            {r.pnp_order_id && (
                              <button
                                data-testid={`gateway-check-copy-btn-${r.id}`}
                                onClick={() => checkGateway(r.pnp_order_id)}
                                disabled={checkingOrder === r.pnp_order_id}
                                className="mt-1 block text-[11px] font-semibold text-slate-400 hover:text-[#0C8695] hover:underline whitespace-nowrap"
                                title="Verify this payment against Plug'n Pay (query_trans)"
                              >
                                {checkingOrder === r.pnp_order_id ? "Checking…" : "Check with gateway"}
                              </button>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.status === "completed" ? (
                              <Badge className="bg-emerald-50 text-emerald-600 hover:bg-emerald-50 border border-emerald-200 whitespace-nowrap">Completed</Badge>
                            ) : r.status === "paid" ? (
                              <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] whitespace-nowrap">To Prepare</Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-600 border-slate-300 whitespace-nowrap">Pending Payment</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.status === "paid" && (
                              <Button
                                size="sm"
                                data-testid={`complete-copy-btn-${r.id}`}
                                onClick={() => completeCopyRequest(r.id)}
                                disabled={completingId === r.id}
                                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg"
                              >
                                {completingId === r.id
                                  ? <Loader2 size={14} className="animate-spin" />
                                  : <><CheckCircle2 size={14} className="mr-1.5" /> Mark Completed</>}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}
          </TabsContent>


          {/* ------------------- BLACKOUT DATES TAB ------------------- */}
          <TabsContent value="blackouts">
            <div className="grid lg:grid-cols-5 gap-6">
              <Card className="rounded-2xl border-slate-100 shadow-sm lg:col-span-2 h-fit">
                <CardContent className="p-7">
                  <h3 className="font-extrabold text-[#0A2540] mb-1">Add a Blackout Date</h3>
                  <p className="text-xs text-slate-500 mb-5">
                    No testing is available on blackout dates — visitors cannot select them when booking online.
                  </p>
                  <div className="space-y-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          data-testid="blackout-date-btn"
                          variant="outline"
                          className="w-full justify-start font-semibold rounded-lg border-slate-200"
                        >
                          <CalendarDays size={16} className="mr-2 text-[#0E9AAA]" />
                          {blackoutDate ? format(blackoutDate, "EEEE, MMMM d, yyyy") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={blackoutDate}
                          onSelect={setBlackoutDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      data-testid="blackout-reason-input"
                      value={blackoutReason}
                      onChange={(e) => setBlackoutReason(e.target.value)}
                      placeholder="Reason (optional, e.g. Public Holiday)"
                      className="rounded-lg"
                    />
                    <Button
                      data-testid="blackout-add-btn"
                      onClick={addBlackout}
                      disabled={addingBlackout}
                      className="w-full bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg"
                    >
                      {addingBlackout ? <Loader2 size={16} className="animate-spin" /> : "Add Blackout Date"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-100 shadow-sm lg:col-span-3">
                <CardContent className="p-7">
                  <h3 className="font-extrabold text-[#0A2540] mb-5">Blackout Dates ({blackoutList.length})</h3>
                  {blackoutList.length === 0 ? (
                    <p className="text-sm text-slate-400 font-semibold" data-testid="blackouts-empty">
                      No blackout dates set — all regular test days are open for booking.
                    </p>
                  ) : (
                    <div className="space-y-2.5">
                      {blackoutList.map((b) => (
                        <div
                          key={b.id}
                          data-testid={`blackout-row-${b.id}`}
                          className="flex items-center justify-between bg-[#F6FAFB] border border-slate-100 rounded-lg px-4 py-3"
                        >
                          <div>
                            <div className="font-bold text-[#0A2540] text-sm">
                              {format(new Date(`${b.date}T12:00:00`), "EEEE, MMMM d, yyyy")}
                            </div>
                            {b.reason && <div className="text-xs text-slate-500 mt-0.5">{b.reason}</div>}
                          </div>
                          <Button
                            data-testid={`blackout-delete-btn-${b.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBlackout(b.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 font-bold rounded-lg"
                          >
                            <XCircle size={15} className="mr-1.5" /> Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

        </Tabs>

        {/* Head shot enlarge dialog */}
        <Dialog open={!!photoView} onOpenChange={(o) => !o && setPhotoView(null)}>
          <DialogContent className="max-w-sm rounded-2xl" data-testid="photo-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0A2540] font-extrabold">{photoView?.name}</DialogTitle>
            </DialogHeader>
            {photoView && (
              <img
                src={photoView.data}
                alt={`Head shot of ${photoView.name}`}
                className="w-full rounded-xl border border-slate-200 object-contain max-h-[420px]"
              />
            )}
            <p className="text-xs text-slate-400">PUBLIC test head shot — verify against passport at check in.</p>
          </DialogContent>
        </Dialog>

        {/* Staff booking edit dialog */}
        <Dialog open={!!editBooking} onOpenChange={(o) => !o && setEditBooking(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0A2540] font-extrabold">
                Edit Booking {editBooking?.confirmation_number}
              </DialogTitle>
            </DialogHeader>
            {editBooking && (
              <div className="space-y-6">
                <p className="text-xs text-slate-500 -mt-1">
                  {editBooking.test_name} • {editBooking.date_display || editBooking.date} at {editBooking.time_slot}
                </p>
                <div className="bg-[#F6FAFB] border border-slate-100 rounded-xl p-4">
                  <div className="text-xs font-extrabold text-[#0E9AAA] uppercase tracking-wide mb-3">Employer / Company</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-bold text-[#0A2540] text-xs">Name</Label>
                      <Input
                        data-testid="edit-employer-name"
                        value={editBooking.employer?.name || ""}
                        onChange={(e) => updateEditEmployer("name", e.target.value)}
                        className="mt-1.5 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <Label className="font-bold text-[#0A2540] text-xs">Contact Number</Label>
                      <Input
                        value={editBooking.employer?.contact || ""}
                        onChange={(e) => updateEditEmployer("contact", e.target.value)}
                        className="mt-1.5 rounded-lg bg-white"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-bold text-[#0A2540] text-xs">Email</Label>
                      <Input
                        type="email"
                        value={editBooking.employer?.email || ""}
                        onChange={(e) => updateEditEmployer("email", e.target.value)}
                        className="mt-1.5 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
                {editBooking.testees.map((t, i) => (
                  <div key={i} className="bg-[#F6FAFB] border border-slate-100 rounded-xl p-4">
                    <div className="text-xs font-extrabold text-[#0E9AAA] uppercase tracking-wide mb-3">
                      Testee {editBooking.testees.length > 1 ? `${i + 1} of ${editBooking.testees.length}` : ""}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label className="font-bold text-[#0A2540] text-xs">Full Name (as in passport)</Label>
                        <Input
                          data-testid={`edit-testee-name-${i}`}
                          value={t.full_name || ""}
                          onChange={(e) => updateEditTestee(i, "full_name", e.target.value)}
                          className="mt-1.5 rounded-lg bg-white"
                        />
                      </div>
                      <div>
                        <Label className="font-bold text-[#0A2540] text-xs">Date of Birth (mm/dd/yyyy)</Label>
                        <Input
                          data-testid={`edit-testee-dob-${i}`}
                          value={t.dob || ""}
                          onChange={(e) => updateEditTestee(i, "dob", e.target.value)}
                          placeholder="mm/dd/yyyy"
                          className="mt-1.5 rounded-lg bg-white"
                        />
                      </div>
                      <div>
                        <Label className="font-bold text-[#0A2540] text-xs">Native Country</Label>
                        <Select value={t.native_country || ""} onValueChange={(v) => updateEditTestee(i, "native_country", v)}>
                          <SelectTrigger className="mt-1.5 rounded-lg bg-white">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold text-[#0A2540] text-xs">Worker Reference #</Label>
                        <Input
                          data-testid={`edit-testee-workerref-${i}`}
                          value={t.worker_ref || ""}
                          onChange={(e) => updateEditTestee(i, "worker_ref", e.target.value)}
                          className="mt-1.5 rounded-lg bg-white"
                        />
                      </div>
                      {t.email !== undefined && t.email !== "" || editBooking.test_id === "toeic-lr-public" ? (
                        <div>
                          <Label className="font-bold text-[#0A2540] text-xs">Testee Email</Label>
                          <Input
                            type="email"
                            value={t.email || ""}
                            onChange={(e) => updateEditTestee(i, "email", e.target.value)}
                            className="mt-1.5 rounded-lg bg-white"
                          />
                        </div>
                      ) : null}
                      <div>
                        <Label className="font-bold text-[#0A2540] text-xs">Taken This Test Before?</Label>
                        <Select value={t.taken_before || "No"} onValueChange={(v) => updateEditTestee(i, "taken_before", v)}>
                          <SelectTrigger className="mt-1.5 rounded-lg bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="No">No</SelectItem>
                            <SelectItem value="Yes">Yes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="font-bold text-[#0A2540] text-xs">For a Work Permit Application?</Label>
                        <Select value={t.work_permit || "Yes"} onValueChange={(v) => updateEditTestee(i, "work_permit", v)}>
                          <SelectTrigger className="mt-1.5 rounded-lg bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end gap-3 pt-1">
                  <Button variant="ghost" onClick={() => setEditBooking(null)} className="font-bold text-slate-500">
                    Discard Changes
                  </Button>
                  <Button
                    data-testid="save-edit-btn"
                    onClick={saveEdit}
                    disabled={savingEdit}
                    className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8"
                  >
                    {savingEdit ? <><Loader2 size={15} className="mr-2 animate-spin" /> Saving...</> : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* CSV import dialog */}
        <Dialog open={importOpen} onOpenChange={(o) => !o && setImportOpen(false)}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#0A2540] font-extrabold">Import Bookings from CSV</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <p className="text-xs text-slate-500">
                Upload a CSV in the MBS roster format. Imported bookings do NOT send confirmation emails,
                and rows already booked for the same date &amp; time are skipped automatically.
              </p>
              <div>
                <Label className="font-bold text-[#0A2540] text-xs">Test Type for These Bookings</Label>
                <Select value={importTest} onValueChange={setImportTest}>
                  <SelectTrigger className="mt-1.5 rounded-lg" data-testid="import-test-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bridge-lr">Redesigned TOEIC Bridge Listening &amp; Reading</SelectItem>
                    <SelectItem value="toeic-lr">TOEIC Listening &amp; Reading</SelectItem>
                    <SelectItem value="toeic-sw">TOEIC Speaking &amp; Writing</SelectItem>
                    <SelectItem value="toeic-speaking">TOEIC Speaking</SelectItem>
                    <SelectItem value="toeic-writing">TOEIC Writing</SelectItem>
                    <SelectItem value="toeic-lr-public">TOEIC Listening &amp; Reading PUBLIC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold text-[#0A2540] text-xs">CSV File</Label>
                <label
                  htmlFor="import-csv-file"
                  className="mt-1.5 cursor-pointer flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0E9AAA] px-5 py-6 text-sm font-bold text-slate-500 hover:text-[#0E9AAA] transition-colors"
                >
                  <Upload size={17} />
                  {importFileName ? `${importFileName} — ${importRows.length} row(s) ready` : "Choose a CSV file"}
                </label>
                <input
                  id="import-csv-file"
                  data-testid="import-file-input"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleImportFile(e.target.files?.[0])}
                />
              </div>
              {importResult && (
                <div className="bg-[#F6FAFB] border border-slate-100 rounded-xl p-4 text-sm">
                  <div className="font-extrabold text-[#0A2540]">
                    Imported {importResult.created} of {importResult.total} bookings
                  </div>
                  {importResult.skipped?.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-500 max-h-40 overflow-y-auto">
                      {importResult.skipped.map((s, i) => (
                        <li key={i}>Row {s.row} ({s.name}): {s.reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setImportOpen(false)} className="font-bold text-slate-500">
                  Close
                </Button>
                <Button
                  data-testid="run-import-btn"
                  onClick={runImport}
                  disabled={importing || !importRows.length}
                  className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8"
                >
                  {importing ? <><Loader2 size={15} className="mr-2 animate-spin" /> Importing...</> : `Import ${importRows.length || ""} Bookings`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
